import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@emotion/react";
import { DateTime } from "luxon";

import { createBirthdayReservation, getBirthdayPageData } from "../../../utils/api";
import AppLayout from "../../../default/components/AppLayout";
import StyledButton from "../../../default/components/StyledButton";
import TicketNotFound from "../../../default/components/TicketNotFound";
import Loading from "../../../default/components/Loading";
import { useUrlParams } from "../../../hooks/useUrlParams";

const withLng = (path, lng) => {
  const hasQuery = path.includes("?");
  return lng ? `${path}${hasQuery ? "&" : "?"}lng=${lng}` : path;
};

const QuestionIcon = ({ onClick }) => (
  <Box
    onClick={onClick}
    sx={(theme) => ({
      width: "16px",
      height: "16px",
      borderRadius: "50%",
      border: `1px solid ${theme.system.colors.text.primary}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      fontSize: "10px",
      color: theme.system.colors.text.primary,
      fontWeight: "bold",
      ml: 1,
      mt: -0.5,
    })}
  >
    ?
  </Box>
);

const CommonDialog = ({ open, onClose, title, children, theme, maxWidth = "350px" }) => (
  <Dialog
    open={open}
    onClose={onClose}
    PaperProps={{ sx: { backgroundColor: "transparent", boxShadow: "none", width: "88%" } }}
  >
    <DialogContent sx={{ padding: 0 }}>
      <Box
        sx={{
          backgroundColor: "rgba(0, 0, 0, 0.9)",
          border: "1px solid rgba(255, 255, 255, 0.6)",
          boxShadow: "0 0 15px rgba(255, 255, 255, 0.4), 0 1px 1px rgba(255, 255, 255, 0.4)",
          borderRadius: "8px",
          padding: "32px 24px",
          maxWidth,
          position: "relative",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: theme.system.colors.text.primary }}
        >
          <CloseIcon />
        </IconButton>
        <Typography
          variant="h6"
          sx={{
            fontFamily: theme.system.fonts.heading,
            fontSize: "16px",
            fontWeight: "bold",
            color: theme.system.colors.text.primary,
            mb: 3,
            textAlign: "center",
          }}
        >
          {title}
        </Typography>
        {children}
      </Box>
    </DialogContent>
  </Dialog>
);

const InfoDialog = ({ open, onClose, type, theme }) => {
  const content = {
    beer: {
      title: "誕生日プレゼント",
      body: (
        <>
          ナズナの誕生日（9月23日）に
          <br />
          お祝いメッセージと共に缶ビールを
          <br />
          お届けします。
          <br />
          <br />
          ナズナから画像を送ってもらう特別クエストが開放されます。
          <br />
          <br />
          交流チケット購入者特典です。追加料金はかかりません。
        </>
      ),
    },
    message: {
      title: "メッセージについて",
      body: (
        <>
          送り主が分かるように、お名前を含め、ナズナに伝えたいお祝いのメッセージを書いてください。
          <br />
          ※50文字以内
        </>
      ),
    },
    delivery: {
      title: "配達日時について",
      body: (
        <>
          選択した時間にプレゼントが届きます。選択した時間に返信ができなくても、
          <br />
          後から返信できます。
        </>
      ),
    },
  }[type] || { title: "", body: "" };

  return (
    <CommonDialog open={open} onClose={onClose} title={content.title} theme={theme}>
      <Typography
        sx={{
          fontFamily: theme.system.fonts.body,
          fontSize: "14px",
          color: theme.system.colors.text.primary,
          lineHeight: 1.8,
          textAlign: "center",
        }}
      >
        {content.body}
      </Typography>
    </CommonDialog>
  );
};

const StatusPage = ({ title, message, children }) => (
  <AppLayout page="Birthday" width="100%">
    <Box
      sx={{
        width: "100%",
        px: 3,
        py: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <Box sx={{ alignSelf: "flex-start", mb: -5 }}>
        <img
          src={process.env.PUBLIC_URL + "/yofukashi/logo.png"}
          alt="logo"
          style={{ height: "70px" }}
        />
      </Box>
      <Box sx={{ mb: -3 }}>
        <img
          src={process.env.PUBLIC_URL + "/yofukashi/beer_drinking.png"}
          alt="beer drinking"
          style={{ maxWidth: "200px", height: "auto" }}
        />
      </Box>
      <Box sx={{ mb: 8, textAlign: "center", width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
        <Typography
          variant="h4"
          sx={(theme) => ({
            fontFamily: theme.system.fonts.heading,
            fontSize: "24px",
            fontWeight: "bold",
            color: theme.system.colors.text.primary,
            lineHeight: 1.5,
          })}
        >
          缶ビール 1本
        </Typography>
      </Box>
      <Typography
        variant="h4"
        sx={(theme) => ({
          mb: 2,
          fontFamily: theme.system.fonts.heading,
          fontSize: "24px",
          fontWeight: "bold",
          color: theme.system.colors.text.primary,
        })}
      >
        {title}
      </Typography>
      {children}
      <Typography
        sx={(theme) => ({
          fontSize: "16px",
          opacity: 0.8,
          whiteSpace: "pre-line",
          color: theme.system.colors.text.primary,
          fontFamily: theme.system.fonts.body,
        })}
      >
        {message}
      </Typography>
    </Box>
  </AppLayout>
);

const ReservationCompleted = ({ reservation }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // The API's timezone-less delivery string is always Japan local time.
  const delivery = typeof reservation?.delivery_time === "string"
    ? DateTime.fromFormat(reservation.delivery_time, "yyyy-MM-dd HH:mm:ss", { zone: "Asia/Tokyo" })
    : null;
  const savedMessage = typeof reservation?.message === "string" ? reservation.message : "";

  return (
    <StatusPage
      title="予約が完了しました"
      message="ご指定の日時に、ナズナへビールとメッセージをお届けします。"
    >
      {(delivery?.isValid || savedMessage) && (
        <Box
          component="dl"
          sx={(theme) => ({
            width: "100%",
            maxWidth: "400px",
            mt: 1,
            mb: 3,
            fontFamily: theme.system.fonts.body,
            fontSize: "16px",
            lineHeight: 1.8,
            color: theme.system.colors.text.primary,
            "& dt": { opacity: 0.8, mb: 0.5 },
            "& dd": { m: 0, whiteSpace: "pre-wrap", overflowWrap: "anywhere" },
            "& dd + dt": { mt: 3 },
          })}
        >
          {delivery?.isValid && (
            <>
              <Box component="dt">配達日時</Box>
              <Box component="dd">
                {delivery.toFormat("yyyy年M月d日 HH:mm")}頃
                <Box component="span" sx={{ display: "inline-block", whiteSpace: "nowrap" }}>（日本時間）</Box>
              </Box>
            </>
          )}
          {savedMessage && (
            <>
              <Box component="dt">メッセージ</Box>
              <Box component="dd">{savedMessage}</Box>
            </>
          )}
        </Box>
      )}
    </StatusPage>
  );
};

function Birthday() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { soulId, lng, work } = useUrlParams();
  const [pageData, setPageData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState("");
  const [deliveryTime, setDeliveryTime] = useState("");
  const [isMessageFocused, setIsMessageFocused] = useState(false);
  const [infoDialog, setInfoDialog] = useState({ open: false, type: null });
  const [dialog, setDialog] = useState({ open: false, title: "", message: "" });

  const loadPage = useCallback(async () => {
    if (!soulId) return;
    setIsLoading(true);
    try {
      const data = await getBirthdayPageData(soulId);
      setPageData(data);
      const available = new Set(
        (data.available_delivery_slots || []).map((slot) => slot.delivery_time)
      );
      setDeliveryTime((current) => (available.has(current) ? current : ""));
    } catch (error) {
      console.error("Error fetching birthday data:", error);
      setDialog({ open: true, title: "エラー", message: "情報を取得できませんでした。" });
    } finally {
      setIsLoading(false);
    }
  }, [soulId]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const handleReserve = async () => {
    if (isProcessing || pageData.registration_closed || !pageData.reservation_open) return;
    const normalized = message.trim();
    if (!normalized || !deliveryTime) {
      setDialog({
        open: true,
        title: "入力エラー",
        message: !normalized
          ? "メッセージを入力してください。"
          : "配達日時を選択してください。",
      });
      return;
    }

    setIsProcessing(true);
    try {
      await createBirthdayReservation(soulId, { message: normalized, deliveryTime });
      await loadPage();
    } catch (error) {
      if (error.status === 409) await loadPage();
      setDialog({
        open: true,
        title: error.status === 409 ? "予約済みです" : "予約できませんでした",
        message:
          error.status === 409
            ? "すでに別の内容で予約されています。予約内容は変更できません。"
            : error.message,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading || !pageData) {
    return <AppLayout page="Birthday" width="100%"><Loading /></AppLayout>;
  }

  if (!pageData.eligible) {
    return (
      <AppLayout page="Birthday" width="88%">
        <TicketNotFound
          onClick={() => navigate(withLng(`/${work}/tickets?soul_id=${soulId}`, lng))}
        />
      </AppLayout>
    );
  }

  if (pageData.already_reserved) {
    return <ReservationCompleted reservation={pageData.reservation} />;
  }

  if (!pageData.reservation_open && !pageData.registration_closed) {
    const startsAt = new Date(pageData.starts_at).toLocaleString("ja-JP", {
      timeZone: "Asia/Tokyo",
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    return <StatusPage title="予約開始前です" message={`予約開始：${startsAt}`} />;
  }

  return (
    <AppLayout page="Birthday" width="100%">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          px: 3,
          py: 2,
        }}
      >
        <Box sx={{ alignSelf: "flex-start", mb: -5 }}>
          <img
            src={process.env.PUBLIC_URL + "/yofukashi/logo.png"}
            alt="logo"
            style={{ height: "70px" }}
          />
        </Box>
        <Box sx={{ mb: -3 }}>
          <img
            src={process.env.PUBLIC_URL + "/yofukashi/beer_drinking.png"}
            alt="beer drinking"
            style={{ maxWidth: "200px", height: "auto" }}
          />
        </Box>

        <Box
          sx={{
            mb: 4,
            textAlign: "center",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start" }}>
            <Typography
              variant="h4"
              sx={(currentTheme) => ({
                fontFamily: currentTheme.system.fonts.heading,
                fontSize: "24px",
                fontWeight: "bold",
                color: currentTheme.system.colors.text.primary,
                lineHeight: 1.5,
              })}
            >
              缶ビール 1本
              <Box component="span" aria-hidden="true" sx={{ display: "block", height: "1.5em" }} />
            </Typography>
            <QuestionIcon onClick={() => setInfoDialog({ open: true, type: "beer" })} />
          </Box>
        </Box>

        <Box
          sx={{
            width: "100%",
            maxWidth: "400px",
            mb: 4,
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={(currentTheme) => ({
                fontFamily: currentTheme.system.fonts.heading,
                fontSize: "22px",
                fontWeight: "bold",
                color: currentTheme.system.colors.text.primary,
              })}
            >
              メッセージ
            </Typography>
            <QuestionIcon onClick={() => setInfoDialog({ open: true, type: "message" })} />
          </Box>
          <Box
            sx={{
              width: "92%",
              backgroundColor: "rgba(0, 0, 0, 0.05)",
              border: "1px solid rgba(255, 255, 255, 0.6)",
              boxShadow:
                "0 0 15px rgba(255, 255, 255, 0.4), 0 1px 1px rgba(255, 255, 255, 0.4)",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "120px",
              padding: "8px",
            }}
          >
            <TextField
              multiline
              fullWidth
              value={message}
              onChange={(event) => setMessage(event.target.value.slice(0, 50))}
              onFocus={() => setIsMessageFocused(true)}
              onBlur={() => setIsMessageFocused(false)}
              placeholder={isMessageFocused ? "" : "誕生日おめでとう！\n-太郎より-"}
              inputProps={{ "aria-label": "メッセージ" }}
              sx={(currentTheme) => ({
                "& .MuiOutlinedInput-root": {
                  border: "none",
                  "& fieldset": { border: "none" },
                  "&:hover fieldset": { border: "none" },
                  "&.Mui-focused fieldset": { border: "none" },
                },
                "& .MuiInputBase-input, & .MuiInputBase-inputMultiline": {
                  textAlign: "center",
                  color: currentTheme.system.colors.text.primary,
                  fontFamily: currentTheme.system.fonts.body,
                },
                "& input::placeholder, & textarea::placeholder": {
                  color: currentTheme.system.colors.text.primary,
                  opacity: 0.7,
                  textAlign: "center",
                  fontFamily: currentTheme.system.fonts.body,
                },
              })}
            />
          </Box>
          <Box sx={{ width: "92%", display: "flex", justifyContent: "flex-end" }}>
            <Typography
              variant="caption"
              sx={(currentTheme) => ({
                mt: 1,
                color: currentTheme.system.colors.text.primary,
                opacity: 0.7,
                fontFamily: currentTheme.system.fonts.body,
              })}
            >
              {message.length}/50
            </Typography>
          </Box>
        </Box>

        <Box sx={{ width: "100%", maxWidth: "400px", mb: 4, textAlign: "center" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={(currentTheme) => ({
                fontFamily: currentTheme.system.fonts.heading,
                fontSize: "22px",
                fontWeight: "bold",
                color: currentTheme.system.colors.text.primary,
              })}
            >
              配達日時
            </Typography>
            <QuestionIcon onClick={() => setInfoDialog({ open: true, type: "delivery" })} />
          </Box>
          <FormControl component="fieldset">
            <RadioGroup value={deliveryTime} onChange={(event) => setDeliveryTime(event.target.value)}>
              {(pageData.available_delivery_slots || []).map((slot, index) => (
                <FormControlLabel
                  key={slot.delivery_time}
                  value={slot.delivery_time}
                  sx={index === 0 ? { mb: 1 } : undefined}
                  control={
                    <Radio
                      sx={{
                        color: "white",
                        "&.Mui-checked": { color: theme.system.colors.text.primary },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        color: theme.system.colors.text.primary,
                        fontFamily: theme.system.fonts.body,
                        fontSize: "16px",
                      }}
                    >
                      {slot.label}
                    </Typography>
                  }
                />
              ))}
            </RadioGroup>
            {pageData.registration_closed && (
              <Typography
                sx={{
                  color: theme.system.colors.text.primary,
                  fontFamily: theme.system.fonts.body,
                  fontSize: "16px",
                  textAlign: "center",
                  opacity: 0.7,
                  mt: 2,
                }}
              >
                配達可能な時間帯が終了しました
              </Typography>
            )}
          </FormControl>
        </Box>

        <StyledButton
          variant="contained"
          onClick={handleReserve}
          disabled={isProcessing || pageData.registration_closed}
          sx={(currentTheme) => ({
            width: "100%",
            maxWidth: "300px",
            fontSize: "18px",
            fontWeight: "bold",
            py: 2,
            mb: 4,
            border: `2px solid ${currentTheme.system.colors.text.primary}`,
            background: isProcessing ? "white" : "transparent",
            opacity: pageData.registration_closed ? 0.5 : 1,
          })}
        >
          プレゼントを予約
        </StyledButton>
      </Box>

      <InfoDialog
        open={infoDialog.open}
        onClose={() => setInfoDialog({ open: false, type: null })}
        type={infoDialog.type}
        theme={theme}
      />

      <CommonDialog
        open={dialog.open}
        onClose={() => setDialog((current) => ({ ...current, open: false }))}
        title={dialog.title}
        theme={theme}
      >
        <Typography
          sx={{
            color: theme.system.colors.text.primary,
            fontFamily: theme.system.fonts.body,
            textAlign: "center",
            whiteSpace: "pre-line",
            fontSize: "14px",
            lineHeight: 1.8,
          }}
        >
          {dialog.message}
        </Typography>
      </CommonDialog>
    </AppLayout>
  );
}

export default Birthday;
